/* tslint:disable */
import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpRequest,
  HttpResponse,
  HttpHeaders,
} from '@angular/common/http';
import { BaseService as __BaseService } from '../base-service';
import { ApiConfiguration as __Configuration } from '../api-configuration';
import { StrictHttpResponse as __StrictHttpResponse } from '../strict-http-response';
import { Observable as __Observable } from 'rxjs';
import { map as __map, filter as __filter } from 'rxjs/operators';

import { Event } from '../models/event';
import { StreamingResponseBody } from '../models/streaming-response-body';
import { HorizontalPodAutoscaler } from '../models/horizontal-pod-autoscaler';
import { Ingress } from '../models/ingress';
import { Pod } from '../models/pod';
import { OverrideObject } from '../models/override-object';
import { OverrideRequest } from '../models/override-request';
import { ResourceHistory } from '../models/resource-history';
import { ResourceOutProperties } from '../models/resource-out-properties';
import { ValidationResponse } from '../models/validation-response';

/**
 * Ui Application Controller
 */
@Injectable({
  providedIn: 'root',
})
class UiApplicationControllerService extends __BaseService {
  static readonly abortUsingPUTPath = '/cc-ui/v1/clusters/{clusterId}/abort';
  static readonly getArgoRolloutInfoUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/argo-info';
  static readonly getEventsUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/pods/{podName}/events';
  static readonly logsUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/pods/{podName}/logs';
  static readonly promoteUsingPUTPath =
    '/cc-ui/v1/clusters/{clusterId}/promote';
  static readonly getHPAUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/resourceName/{applicationName}/hpa';
  static readonly getIngressesUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/resourceName/{applicationName}/ingresses';
  static readonly listPodsUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/resourceName/{applicationName}/pods';
  static readonly getResourceByNameV2UsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/resourceType/{resourceType}/resourceName/{resourceName}';
  static readonly getDeployedCommitIdForResourceUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/resourceType/{resourceType}/resourceName/{resourceName}/deployed-commit-id';
  static readonly getResourceOverrideObjectUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/resourceType/{resourceType}/resourceName/{resourceName}/overrides';
  static readonly postResourceOverrideObjectUsingPOSTPath =
    '/cc-ui/v1/clusters/{clusterId}/resourceType/{resourceType}/resourceName/{resourceName}/overrides';
  static readonly getResourceHistoryUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/resourceType/{resourceType}/resourceName/{resourceName}/resource-history';
  static readonly getResourceOutPropertiesUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/resourceType/{resourceType}/resourceName/{resourceName}/resource-out-properties';
  static readonly rollingRestartUsingPOSTPath =
    '/cc-ui/v1/clusters/{clusterId}/restart/{applicationName}';
  static readonly clusterSyncWithGitUsingPOSTPath =
    '/cc-ui/v1/clusters/{clusterId}/sync-with-git';
  static readonly runValidationUsingPOSTPath =
    '/cc-ui/v1/clusters/{clusterId}/validate';
  static readonly getValidationsUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/validation-errors';
  static readonly getResourceByNameUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/{resourceType}/{appName}';
  static readonly getApplicationOverridesUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/{resourceType}/{appName}/overrides';

  constructor(config: __Configuration, http: HttpClient) {
    super(config, http);
  }

  /**
   * abort
   * @param params The `UiApplicationControllerService.AbortUsingPUTParams` containing the following parameters:
   *
   * - `labels`: labels
   *
   * - `clusterId`: clusterId
   */
  abortUsingPUTResponse(
    params: UiApplicationControllerService.AbortUsingPUTParams,
  ): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    if (params.labels != null)
      __params = __params.set('labels', params.labels.toString());

    let req = new HttpRequest<any>(
      'PUT',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/abort`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      }),
    );
  }
  /**
   * abort
   * @param params The `UiApplicationControllerService.AbortUsingPUTParams` containing the following parameters:
   *
   * - `labels`: labels
   *
   * - `clusterId`: clusterId
   */
  abortUsingPUT(
    params: UiApplicationControllerService.AbortUsingPUTParams,
  ): __Observable<null> {
    return this.abortUsingPUTResponse(params).pipe(
      __map((_r) => _r.body as null),
    );
  }

  /**
   * getArgoRolloutInfo
   * @param params The `UiApplicationControllerService.GetArgoRolloutInfoUsingGETParams` containing the following parameters:
   *
   * - `labels`: labels
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  getArgoRolloutInfoUsingGETResponse(
    params: UiApplicationControllerService.GetArgoRolloutInfoUsingGETParams,
  ): __Observable<__StrictHttpResponse<{}>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    if (params.labels != null)
      __params = __params.set('labels', params.labels.toString());

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/argo-info`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<{}>;
      }),
    );
  }
  /**
   * getArgoRolloutInfo
   * @param params The `UiApplicationControllerService.GetArgoRolloutInfoUsingGETParams` containing the following parameters:
   *
   * - `labels`: labels
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  getArgoRolloutInfoUsingGET(
    params: UiApplicationControllerService.GetArgoRolloutInfoUsingGETParams,
  ): __Observable<{}> {
    return this.getArgoRolloutInfoUsingGETResponse(params).pipe(
      __map((_r) => _r.body as {}),
    );
  }

  /**
   * getEvents
   * @param params The `UiApplicationControllerService.GetEventsUsingGETParams` containing the following parameters:
   *
   * - `podName`: podName
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  getEventsUsingGETResponse(
    params: UiApplicationControllerService.GetEventsUsingGETParams,
  ): __Observable<__StrictHttpResponse<Array<Event>>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/pods/${encodeURIComponent(String(params.podName))}/events`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Array<Event>>;
      }),
    );
  }
  /**
   * getEvents
   * @param params The `UiApplicationControllerService.GetEventsUsingGETParams` containing the following parameters:
   *
   * - `podName`: podName
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  getEventsUsingGET(
    params: UiApplicationControllerService.GetEventsUsingGETParams,
  ): __Observable<Array<Event>> {
    return this.getEventsUsingGETResponse(params).pipe(
      __map((_r) => _r.body as Array<Event>),
    );
  }

  /**
   * logs
   * @param params The `UiApplicationControllerService.LogsUsingGETParams` containing the following parameters:
   *
   * - `podName`: podName
   *
   * - `labels`: labels
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  logsUsingGETResponse(
    params: UiApplicationControllerService.LogsUsingGETParams,
  ): __Observable<__StrictHttpResponse<StreamingResponseBody>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    if (params.labels != null)
      __params = __params.set('labels', params.labels.toString());

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/pods/${encodeURIComponent(String(params.podName))}/logs`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<StreamingResponseBody>;
      }),
    );
  }
  /**
   * logs
   * @param params The `UiApplicationControllerService.LogsUsingGETParams` containing the following parameters:
   *
   * - `podName`: podName
   *
   * - `labels`: labels
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  logsUsingGET(
    params: UiApplicationControllerService.LogsUsingGETParams,
  ): __Observable<StreamingResponseBody> {
    return this.logsUsingGETResponse(params).pipe(
      __map((_r) => _r.body as StreamingResponseBody),
    );
  }

  /**
   * promote
   * @param params The `UiApplicationControllerService.PromoteUsingPUTParams` containing the following parameters:
   *
   * - `labels`: labels
   *
   * - `clusterId`: clusterId
   */
  promoteUsingPUTResponse(
    params: UiApplicationControllerService.PromoteUsingPUTParams,
  ): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    if (params.labels != null)
      __params = __params.set('labels', params.labels.toString());

    let req = new HttpRequest<any>(
      'PUT',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/promote`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      }),
    );
  }
  /**
   * promote
   * @param params The `UiApplicationControllerService.PromoteUsingPUTParams` containing the following parameters:
   *
   * - `labels`: labels
   *
   * - `clusterId`: clusterId
   */
  promoteUsingPUT(
    params: UiApplicationControllerService.PromoteUsingPUTParams,
  ): __Observable<null> {
    return this.promoteUsingPUTResponse(params).pipe(
      __map((_r) => _r.body as null),
    );
  }

  /**
   * getHPA
   * @param params The `UiApplicationControllerService.GetHPAUsingGETParams` containing the following parameters:
   *
   * - `clusterId`: clusterId
   *
   * - `applicationName`: applicationName
   *
   * @return OK
   */
  getHPAUsingGETResponse(
    params: UiApplicationControllerService.GetHPAUsingGETParams,
  ): __Observable<__StrictHttpResponse<HorizontalPodAutoscaler>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/resourceName/${encodeURIComponent(
          String(params.applicationName),
        )}/hpa`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<HorizontalPodAutoscaler>;
      }),
    );
  }
  /**
   * getHPA
   * @param params The `UiApplicationControllerService.GetHPAUsingGETParams` containing the following parameters:
   *
   * - `clusterId`: clusterId
   *
   * - `applicationName`: applicationName
   *
   * @return OK
   */
  getHPAUsingGET(
    params: UiApplicationControllerService.GetHPAUsingGETParams,
  ): __Observable<HorizontalPodAutoscaler> {
    return this.getHPAUsingGETResponse(params).pipe(
      __map((_r) => _r.body as HorizontalPodAutoscaler),
    );
  }

  /**
   * getIngresses
   * @param params The `UiApplicationControllerService.GetIngressesUsingGETParams` containing the following parameters:
   *
   * - `clusterId`: clusterId
   *
   * - `applicationName`: applicationName
   *
   * @return OK
   */
  getIngressesUsingGETResponse(
    params: UiApplicationControllerService.GetIngressesUsingGETParams,
  ): __Observable<__StrictHttpResponse<Array<Ingress>>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/resourceName/${encodeURIComponent(
          String(params.applicationName),
        )}/ingresses`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Array<Ingress>>;
      }),
    );
  }
  /**
   * getIngresses
   * @param params The `UiApplicationControllerService.GetIngressesUsingGETParams` containing the following parameters:
   *
   * - `clusterId`: clusterId
   *
   * - `applicationName`: applicationName
   *
   * @return OK
   */
  getIngressesUsingGET(
    params: UiApplicationControllerService.GetIngressesUsingGETParams,
  ): __Observable<Array<Ingress>> {
    return this.getIngressesUsingGETResponse(params).pipe(
      __map((_r) => _r.body as Array<Ingress>),
    );
  }

  /**
   * listPods
   * @param params The `UiApplicationControllerService.ListPodsUsingGETParams` containing the following parameters:
   *
   * - `clusterId`: clusterId
   *
   * - `applicationName`: applicationName
   *
   * @return OK
   */
  listPodsUsingGETResponse(
    params: UiApplicationControllerService.ListPodsUsingGETParams,
  ): __Observable<__StrictHttpResponse<Array<Pod>>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/resourceName/${encodeURIComponent(
          String(params.applicationName),
        )}/pods`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Array<Pod>>;
      }),
    );
  }
  /**
   * listPods
   * @param params The `UiApplicationControllerService.ListPodsUsingGETParams` containing the following parameters:
   *
   * - `clusterId`: clusterId
   *
   * - `applicationName`: applicationName
   *
   * @return OK
   */
  listPodsUsingGET(
    params: UiApplicationControllerService.ListPodsUsingGETParams,
  ): __Observable<Array<Pod>> {
    return this.listPodsUsingGETResponse(params).pipe(
      __map((_r) => _r.body as Array<Pod>),
    );
  }

  /**
   * getResourceByNameV2
   * @param params The `UiApplicationControllerService.GetResourceByNameV2UsingGETParams` containing the following parameters:
   *
   * - `resourceType`: resourceType
   *
   * - `resourceName`: resourceName
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  getResourceByNameV2UsingGETResponse(
    params: UiApplicationControllerService.GetResourceByNameV2UsingGETParams,
  ): __Observable<__StrictHttpResponse<{}>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/resourceType/${encodeURIComponent(
          String(params.resourceType),
        )}/resourceName/${encodeURIComponent(String(params.resourceName))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<{}>;
      }),
    );
  }
  /**
   * getResourceByNameV2
   * @param params The `UiApplicationControllerService.GetResourceByNameV2UsingGETParams` containing the following parameters:
   *
   * - `resourceType`: resourceType
   *
   * - `resourceName`: resourceName
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  getResourceByNameV2UsingGET(
    params: UiApplicationControllerService.GetResourceByNameV2UsingGETParams,
  ): __Observable<{}> {
    return this.getResourceByNameV2UsingGETResponse(params).pipe(
      __map((_r) => _r.body as {}),
    );
  }

  /**
   * getDeployedCommitIdForResource
   * @param params The `UiApplicationControllerService.GetDeployedCommitIdForResourceUsingGETParams` containing the following parameters:
   *
   * - `resourceType`: resourceType
   *
   * - `resourceName`: resourceName
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  getDeployedCommitIdForResourceUsingGETResponse(
    params: UiApplicationControllerService.GetDeployedCommitIdForResourceUsingGETParams,
  ): __Observable<__StrictHttpResponse<string>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/resourceType/${encodeURIComponent(
          String(params.resourceType),
        )}/resourceName/${encodeURIComponent(
          String(params.resourceName),
        )}/deployed-commit-id`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'text',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<string>;
      }),
    );
  }
  /**
   * getDeployedCommitIdForResource
   * @param params The `UiApplicationControllerService.GetDeployedCommitIdForResourceUsingGETParams` containing the following parameters:
   *
   * - `resourceType`: resourceType
   *
   * - `resourceName`: resourceName
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  getDeployedCommitIdForResourceUsingGET(
    params: UiApplicationControllerService.GetDeployedCommitIdForResourceUsingGETParams,
  ): __Observable<string> {
    return this.getDeployedCommitIdForResourceUsingGETResponse(params).pipe(
      __map((_r) => _r.body as string),
    );
  }

  /**
   * getResourceOverrideObject
   * @param params The `UiApplicationControllerService.GetResourceOverrideObjectUsingGETParams` containing the following parameters:
   *
   * - `resourceType`: resourceType
   *
   * - `resourceName`: resourceName
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  getResourceOverrideObjectUsingGETResponse(
    params: UiApplicationControllerService.GetResourceOverrideObjectUsingGETParams,
  ): __Observable<__StrictHttpResponse<OverrideObject>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/resourceType/${encodeURIComponent(
          String(params.resourceType),
        )}/resourceName/${encodeURIComponent(
          String(params.resourceName),
        )}/overrides`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<OverrideObject>;
      }),
    );
  }
  /**
   * getResourceOverrideObject
   * @param params The `UiApplicationControllerService.GetResourceOverrideObjectUsingGETParams` containing the following parameters:
   *
   * - `resourceType`: resourceType
   *
   * - `resourceName`: resourceName
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  getResourceOverrideObjectUsingGET(
    params: UiApplicationControllerService.GetResourceOverrideObjectUsingGETParams,
  ): __Observable<OverrideObject> {
    return this.getResourceOverrideObjectUsingGETResponse(params).pipe(
      __map((_r) => _r.body as OverrideObject),
    );
  }

  /**
   * postResourceOverrideObject
   * @param params The `UiApplicationControllerService.PostResourceOverrideObjectUsingPOSTParams` containing the following parameters:
   *
   * - `resourceType`: resourceType
   *
   * - `resourceName`: resourceName
   *
   * - `overrideRequest`: overrideRequest
   *
   * - `clusterId`: clusterId
   *
   * - `doSync`: doSync
   *
   * @return OK
   */
  postResourceOverrideObjectUsingPOSTResponse(
    params: UiApplicationControllerService.PostResourceOverrideObjectUsingPOSTParams,
  ): __Observable<__StrictHttpResponse<OverrideObject>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    __body = params.overrideRequest;

    if (params.doSync != null)
      __params = __params.set('doSync', params.doSync.toString());
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/resourceType/${encodeURIComponent(
          String(params.resourceType),
        )}/resourceName/${encodeURIComponent(
          String(params.resourceName),
        )}/overrides`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<OverrideObject>;
      }),
    );
  }
  /**
   * postResourceOverrideObject
   * @param params The `UiApplicationControllerService.PostResourceOverrideObjectUsingPOSTParams` containing the following parameters:
   *
   * - `resourceType`: resourceType
   *
   * - `resourceName`: resourceName
   *
   * - `overrideRequest`: overrideRequest
   *
   * - `clusterId`: clusterId
   *
   * - `doSync`: doSync
   *
   * @return OK
   */
  postResourceOverrideObjectUsingPOST(
    params: UiApplicationControllerService.PostResourceOverrideObjectUsingPOSTParams,
  ): __Observable<OverrideObject> {
    return this.postResourceOverrideObjectUsingPOSTResponse(params).pipe(
      __map((_r) => _r.body as OverrideObject),
    );
  }

  /**
   * getResourceHistory
   * @param params The `UiApplicationControllerService.GetResourceHistoryUsingGETParams` containing the following parameters:
   *
   * - `resourceType`: resourceType
   *
   * - `resourceName`: resourceName
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  getResourceHistoryUsingGETResponse(
    params: UiApplicationControllerService.GetResourceHistoryUsingGETParams,
  ): __Observable<__StrictHttpResponse<ResourceHistory>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/resourceType/${encodeURIComponent(
          String(params.resourceType),
        )}/resourceName/${encodeURIComponent(
          String(params.resourceName),
        )}/resource-history`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<ResourceHistory>;
      }),
    );
  }
  /**
   * getResourceHistory
   * @param params The `UiApplicationControllerService.GetResourceHistoryUsingGETParams` containing the following parameters:
   *
   * - `resourceType`: resourceType
   *
   * - `resourceName`: resourceName
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  getResourceHistoryUsingGET(
    params: UiApplicationControllerService.GetResourceHistoryUsingGETParams,
  ): __Observable<ResourceHistory> {
    return this.getResourceHistoryUsingGETResponse(params).pipe(
      __map((_r) => _r.body as ResourceHistory),
    );
  }

  /**
   * getResourceOutProperties
   * @param params The `UiApplicationControllerService.GetResourceOutPropertiesUsingGETParams` containing the following parameters:
   *
   * - `resourceType`: resourceType
   *
   * - `resourceName`: resourceName
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  getResourceOutPropertiesUsingGETResponse(
    params: UiApplicationControllerService.GetResourceOutPropertiesUsingGETParams,
  ): __Observable<__StrictHttpResponse<ResourceOutProperties>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/resourceType/${encodeURIComponent(
          String(params.resourceType),
        )}/resourceName/${encodeURIComponent(
          String(params.resourceName),
        )}/resource-out-properties`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<ResourceOutProperties>;
      }),
    );
  }
  /**
   * getResourceOutProperties
   * @param params The `UiApplicationControllerService.GetResourceOutPropertiesUsingGETParams` containing the following parameters:
   *
   * - `resourceType`: resourceType
   *
   * - `resourceName`: resourceName
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  getResourceOutPropertiesUsingGET(
    params: UiApplicationControllerService.GetResourceOutPropertiesUsingGETParams,
  ): __Observable<ResourceOutProperties> {
    return this.getResourceOutPropertiesUsingGETResponse(params).pipe(
      __map((_r) => _r.body as ResourceOutProperties),
    );
  }

  /**
   * rollingRestart
   * @param params The `UiApplicationControllerService.RollingRestartUsingPOSTParams` containing the following parameters:
   *
   * - `labels`: labels
   *
   * - `clusterId`: clusterId
   *
   * - `applicationName`: applicationName
   */
  rollingRestartUsingPOSTResponse(
    params: UiApplicationControllerService.RollingRestartUsingPOSTParams,
  ): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    if (params.labels != null)
      __params = __params.set('labels', params.labels.toString());

    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/restart/${encodeURIComponent(String(params.applicationName))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      }),
    );
  }
  /**
   * rollingRestart
   * @param params The `UiApplicationControllerService.RollingRestartUsingPOSTParams` containing the following parameters:
   *
   * - `labels`: labels
   *
   * - `clusterId`: clusterId
   *
   * - `applicationName`: applicationName
   */
  rollingRestartUsingPOST(
    params: UiApplicationControllerService.RollingRestartUsingPOSTParams,
  ): __Observable<null> {
    return this.rollingRestartUsingPOSTResponse(params).pipe(
      __map((_r) => _r.body as null),
    );
  }

  /**
   * clusterSyncWithGit
   * @param params The `UiApplicationControllerService.ClusterSyncWithGitUsingPOSTParams` containing the following parameters:
   *
   * - `clusterId`: clusterId
   *
   * - `force`: force
   */
  clusterSyncWithGitUsingPOSTResponse(
    params: UiApplicationControllerService.ClusterSyncWithGitUsingPOSTParams,
  ): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    if (params.force != null)
      __params = __params.set('force', params.force.toString());
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/sync-with-git`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      }),
    );
  }
  /**
   * clusterSyncWithGit
   * @param params The `UiApplicationControllerService.ClusterSyncWithGitUsingPOSTParams` containing the following parameters:
   *
   * - `clusterId`: clusterId
   *
   * - `force`: force
   */
  clusterSyncWithGitUsingPOST(
    params: UiApplicationControllerService.ClusterSyncWithGitUsingPOSTParams,
  ): __Observable<null> {
    return this.clusterSyncWithGitUsingPOSTResponse(params).pipe(
      __map((_r) => _r.body as null),
    );
  }

  /**
   * runValidation
   * @param clusterId clusterId
   */
  runValidationUsingPOSTResponse(
    clusterId: string,
  ): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(String(clusterId))}/validate`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      }),
    );
  }
  /**
   * runValidation
   * @param clusterId clusterId
   */
  runValidationUsingPOST(clusterId: string): __Observable<null> {
    return this.runValidationUsingPOSTResponse(clusterId).pipe(
      __map((_r) => _r.body as null),
    );
  }

  /**
   * getValidations
   * @param clusterId clusterId
   * @return OK
   */
  getValidationsUsingGETResponse(
    clusterId: string,
  ): __Observable<__StrictHttpResponse<Array<ValidationResponse>>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(clusterId),
        )}/validation-errors`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Array<ValidationResponse>>;
      }),
    );
  }
  /**
   * getValidations
   * @param clusterId clusterId
   * @return OK
   */
  getValidationsUsingGET(
    clusterId: string,
  ): __Observable<Array<ValidationResponse>> {
    return this.getValidationsUsingGETResponse(clusterId).pipe(
      __map((_r) => _r.body as Array<ValidationResponse>),
    );
  }

  /**
   * getResourceByName
   * @param params The `UiApplicationControllerService.GetResourceByNameUsingGETParams` containing the following parameters:
   *
   * - `resourceType`: resourceType
   *
   * - `clusterId`: clusterId
   *
   * - `appName`: appName
   *
   * @return OK
   */
  getResourceByNameUsingGETResponse(
    params: UiApplicationControllerService.GetResourceByNameUsingGETParams,
  ): __Observable<__StrictHttpResponse<{}>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/${encodeURIComponent(
          String(params.resourceType),
        )}/${encodeURIComponent(String(params.appName))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<{}>;
      }),
    );
  }
  /**
   * getResourceByName
   * @param params The `UiApplicationControllerService.GetResourceByNameUsingGETParams` containing the following parameters:
   *
   * - `resourceType`: resourceType
   *
   * - `clusterId`: clusterId
   *
   * - `appName`: appName
   *
   * @return OK
   */
  getResourceByNameUsingGET(
    params: UiApplicationControllerService.GetResourceByNameUsingGETParams,
  ): __Observable<{}> {
    return this.getResourceByNameUsingGETResponse(params).pipe(
      __map((_r) => _r.body as {}),
    );
  }

  /**
   * getApplicationOverrides
   * @param params The `UiApplicationControllerService.GetApplicationOverridesUsingGETParams` containing the following parameters:
   *
   * - `resourceType`: resourceType
   *
   * - `clusterId`: clusterId
   *
   * - `appName`: appName
   *
   * @return OK
   */
  getApplicationOverridesUsingGETResponse(
    params: UiApplicationControllerService.GetApplicationOverridesUsingGETParams,
  ): __Observable<__StrictHttpResponse<{}>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/${encodeURIComponent(
          String(params.resourceType),
        )}/${encodeURIComponent(String(params.appName))}/overrides`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<{}>;
      }),
    );
  }
  /**
   * getApplicationOverrides
   * @param params The `UiApplicationControllerService.GetApplicationOverridesUsingGETParams` containing the following parameters:
   *
   * - `resourceType`: resourceType
   *
   * - `clusterId`: clusterId
   *
   * - `appName`: appName
   *
   * @return OK
   */
  getApplicationOverridesUsingGET(
    params: UiApplicationControllerService.GetApplicationOverridesUsingGETParams,
  ): __Observable<{}> {
    return this.getApplicationOverridesUsingGETResponse(params).pipe(
      __map((_r) => _r.body as {}),
    );
  }
}

module UiApplicationControllerService {
  /**
   * Parameters for abortUsingPUT
   */
  export interface AbortUsingPUTParams {
    /**
     * labels
     */
    labels: any;

    /**
     * clusterId
     */
    clusterId: string;
  }

  /**
   * Parameters for getArgoRolloutInfoUsingGET
   */
  export interface GetArgoRolloutInfoUsingGETParams {
    /**
     * labels
     */
    labels: any;

    /**
     * clusterId
     */
    clusterId: string;
  }

  /**
   * Parameters for getEventsUsingGET
   */
  export interface GetEventsUsingGETParams {
    /**
     * podName
     */
    podName: string;

    /**
     * clusterId
     */
    clusterId: string;
  }

  /**
   * Parameters for logsUsingGET
   */
  export interface LogsUsingGETParams {
    /**
     * podName
     */
    podName: string;

    /**
     * labels
     */
    labels: any;

    /**
     * clusterId
     */
    clusterId: string;
  }

  /**
   * Parameters for promoteUsingPUT
   */
  export interface PromoteUsingPUTParams {
    /**
     * labels
     */
    labels: any;

    /**
     * clusterId
     */
    clusterId: string;
  }

  /**
   * Parameters for getHPAUsingGET
   */
  export interface GetHPAUsingGETParams {
    /**
     * clusterId
     */
    clusterId: string;

    /**
     * applicationName
     */
    applicationName: string;
  }

  /**
   * Parameters for getIngressesUsingGET
   */
  export interface GetIngressesUsingGETParams {
    /**
     * clusterId
     */
    clusterId: string;

    /**
     * applicationName
     */
    applicationName: string;
  }

  /**
   * Parameters for listPodsUsingGET
   */
  export interface ListPodsUsingGETParams {
    /**
     * clusterId
     */
    clusterId: string;

    /**
     * applicationName
     */
    applicationName: string;
  }

  /**
   * Parameters for getResourceByNameV2UsingGET
   */
  export interface GetResourceByNameV2UsingGETParams {
    /**
     * resourceType
     */
    resourceType: string;

    /**
     * resourceName
     */
    resourceName: string;

    /**
     * clusterId
     */
    clusterId: string;
  }

  /**
   * Parameters for getDeployedCommitIdForResourceUsingGET
   */
  export interface GetDeployedCommitIdForResourceUsingGETParams {
    /**
     * resourceType
     */
    resourceType: string;

    /**
     * resourceName
     */
    resourceName: string;

    /**
     * clusterId
     */
    clusterId: string;
  }

  /**
   * Parameters for getResourceOverrideObjectUsingGET
   */
  export interface GetResourceOverrideObjectUsingGETParams {
    /**
     * resourceType
     */
    resourceType: string;

    /**
     * resourceName
     */
    resourceName: string;

    /**
     * clusterId
     */
    clusterId: string;
  }

  /**
   * Parameters for postResourceOverrideObjectUsingPOST
   */
  export interface PostResourceOverrideObjectUsingPOSTParams {
    /**
     * resourceType
     */
    resourceType: string;

    /**
     * resourceName
     */
    resourceName: string;

    /**
     * overrideRequest
     */
    overrideRequest: OverrideRequest;

    /**
     * clusterId
     */
    clusterId: string;

    /**
     * doSync
     */
    doSync?: boolean;
  }

  /**
   * Parameters for getResourceHistoryUsingGET
   */
  export interface GetResourceHistoryUsingGETParams {
    /**
     * resourceType
     */
    resourceType: string;

    /**
     * resourceName
     */
    resourceName: string;

    /**
     * clusterId
     */
    clusterId: string;
  }

  /**
   * Parameters for getResourceOutPropertiesUsingGET
   */
  export interface GetResourceOutPropertiesUsingGETParams {
    /**
     * resourceType
     */
    resourceType: string;

    /**
     * resourceName
     */
    resourceName: string;

    /**
     * clusterId
     */
    clusterId: string;
  }

  /**
   * Parameters for rollingRestartUsingPOST
   */
  export interface RollingRestartUsingPOSTParams {
    /**
     * labels
     */
    labels: any;

    /**
     * clusterId
     */
    clusterId: string;

    /**
     * applicationName
     */
    applicationName: string;
  }

  /**
   * Parameters for clusterSyncWithGitUsingPOST
   */
  export interface ClusterSyncWithGitUsingPOSTParams {
    /**
     * clusterId
     */
    clusterId: string;

    /**
     * force
     */
    force?: boolean;
  }

  /**
   * Parameters for getResourceByNameUsingGET
   */
  export interface GetResourceByNameUsingGETParams {
    /**
     * resourceType
     */
    resourceType: string;

    /**
     * clusterId
     */
    clusterId: string;

    /**
     * appName
     */
    appName: string;
  }

  /**
   * Parameters for getApplicationOverridesUsingGET
   */
  export interface GetApplicationOverridesUsingGETParams {
    /**
     * resourceType
     */
    resourceType: string;

    /**
     * clusterId
     */
    clusterId: string;

    /**
     * appName
     */
    appName: string;
  }
}

export { UiApplicationControllerService };
